import React, {useCallback, useContext, useEffect, useState} from 'react';
import SectionHeader from '../../../util/component/SectionHeader';
import {Box, Button, Grid, Paper, useTheme} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {GlobalContext} from '../../../util/state/GlobalContextProvider';
import * as yup from 'yup';
import {FormProvider, useForm} from 'react-hook-form';
import OperatorData from '../component/OperatorData';
import CustomContentWrapper from '../../../util/component/CustomContentWrapper';
import {yupResolver} from "@hookform/resolvers/yup";
import axios from "axios";
import {setSnackbar} from "../../../util/state/globalAction";
import {snackbarTypes} from "../../../util/state/globalVariables";
import {useMutation, useQuery} from "@tanstack/react-query";
import CustomLoader from "../../../util/component/CustomLoader";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {operatorDetailStructure} from "../state/operatorState";
import {useHasScope} from "../../../config/globalConfig";
import NotAuthorizedDialog from "../../authentication/component/NotAuthorizedDialog";

interface OperatorCreateProps {
    username: string | null;
    password: string | null;
    name: string | null;
    surname: string | null;
    email: string | null;
    operatorType: number | null;
    legalEntities: any[];
    dealers: any[];
    mobilityStores: any[];
    logisticCompounds: any[];
    departments: any[];
    profiles: any[];
}

const OperatorCreate = () => {
    const hasScope = useHasScope;
    const theme = useTheme();
    const {t} = useTranslation(['operator']);
    const {globalData, dispatchGlobalData} = useContext(GlobalContext);
    const [legalEntities] = useState<any[]>(globalData.user?.legalEntities || []);
    const [dealers] = useState<any[]>(globalData.user?.dealers || []);
    const [mobilityStores] = useState<any[]>(globalData.user?.mobilityStores || []);
    const [logisticCompounds] = useState<any[]>(globalData.user?.logisticCompounds || []);
    const [departments] = useState<any[]>(globalData.user?.departments || []);
    const [profiles, setProfiles] = useState<any[]>([]);
    const [operatorTypes, setOperatorTypes] = useState<any[]>([]);
    const setOperatorDetailStructure = useSetRecoilState(operatorDetailStructure);
    const {profileDtos} = useRecoilValue(operatorDetailStructure);

    useEffect(() => {
        setProfiles(profileDtos);
    }, [profileDtos]);

    useEffect(() => {
        setOperatorTypes([
            {code: 0, name: t('label.operatorType_0')},
            {code: 1, name: t('label.operatorType_1')},
            {code: 2, name: t('label.operatorType_2')}
        ])
    }, [t]);

    const validationSchema = yup.object().shape({
        username: yup
            .string()
            .nullable(true)
            .trim()
            .lowercase()
            .required('message.validation.fieldRequired'),
        password: yup
            .string()
            .nullable(true)
            .trim()
            .required('message.validation.fieldRequired'),
        name: yup
            .string()
            .nullable(true)
            .trim()
            .lowercase()
            .required('message.validation.fieldRequired'),
        surname: yup
            .string()
            .nullable(true)
            .trim()
            .lowercase()
            .required('message.validation.fieldRequired'),
        email: yup
            .string()
            .nullable(true)
            .trim()
            .lowercase()
            .required('message.validation.fieldRequired')
            .email('message.validation.emailNotValid'),
        operatorType: yup
            .number()
            .nullable(true)
            .required('message.validation.fieldRequired'),
        legalEntities: yup
            .array()
            .min(1, 'message.validation.fieldRequired'),
        dealers: yup
            .array()
            .min(1, 'message.validation.fieldRequired'),
        departments: yup
            .array()
            .min(1, 'message.validation.fieldRequired'),
        profiles: yup
            .array().min(1, 'message.validation.fieldRequired'),
    });

    const methods = useForm<OperatorCreateProps>({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            username: null,
            // @ts-ignore
            password: !JSON.parse(window?.appConfig?.ADFS_ENABLED.toLowerCase()) ? null : (Math.random() + 1).toString(36).substring(2),
            name: null,
            surname: null,
            email: null,
            operatorType: 0,
            legalEntities: legalEntities.length === 1 ? legalEntities : [],
            dealers: dealers.length === 1 ? dealers : [],
            mobilityStores: mobilityStores.length === 1 ? mobilityStores : [],
            logisticCompounds: logisticCompounds.length === 1 ? logisticCompounds : [],
            departments: departments.length === 1 ? departments : [],
            profiles: [],
        },
    });

    const {handleSubmit} = methods;

    const createOnSuccess = useCallback((response: any) => {
        if (response.status === 207) {
            let messages = JSON.parse(response.data.errorDetails) as string[];
            let message = '';
            messages.forEach((m) => {
                message = message.concat('\n').concat(t('message.warning.'.concat(m)));
            });
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: message,
                        type: snackbarTypes.warning,
                        show: true,
                        autoClose: true,
                    },
                },
            });
        } else {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('translation:success.createOperation'),
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true,
                    },
                },
            });
        }
    }, [dispatchGlobalData, t]);

    const createOnError = useCallback((error: any) => {
        console.log(error);
        if (error.response.status === 400) {
            let errorMsg = t('translation:error.badRequest');
            JSON.parse(error.response.data.errorDetails).forEach((e: string) => {
                errorMsg = errorMsg.concat('\n').concat(t('message.error.'.concat(e)));
            });
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: errorMsg,
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    },
                },
            });
        } else {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('translation:error.createOperation'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    },
                },
            });
        }
    }, [dispatchGlobalData, t]);

    const createOperator = useMutation({
        mutationFn: (params: any) => {
            return axios({
                method: 'post',
                url: '/operator',
                data: params,
            })
        },
        onSuccess: createOnSuccess,
        onError: createOnError
    })

    const findOperatorDetailStructure = () => {
        return axios.get('/operator/structure')
    }
    const onSuccess = (response: any) => {
        setOperatorDetailStructure(response.data);
    }
    const onError = (error: any) => {
        console.log(error);
        dispatchGlobalData({
            type: setSnackbar,
            payload: {
                snackbar: {
                    message: t('message.error.tryLater'),
                    type: snackbarTypes.error,
                    show: true,
                    autoClose: true
                }
            }
        });
    }

    const {isFetching} = useQuery(['findOperatorDetailStructure'], findOperatorDetailStructure, {
        onSuccess,
        onError
    })

    return (

        <Box>
            <SectionHeader
                title={t('label.createOperator')}
            />
            <CustomContentWrapper>
                {hasScope('SCOPE_CREATE_OPERATOR') ? <FormProvider {...methods}>
                        <form onSubmit={handleSubmit((params: any) => createOperator.mutate(params))}>
                            <Paper style={{
                                borderRadius: "4px",
                                padding: theme.spacing(1) + ' ' + theme.spacing(2),
                                marginBottom: theme.spacing(2)
                            }}
                                   elevation={1}
                            >
                                <Grid container spacing={2}>
                                    <OperatorData
                                        legalEntities={legalEntities}
                                        dealers={dealers}
                                        departments={departments}
                                        mobilityStores={mobilityStores}
                                        logisticCompounds={logisticCompounds}
                                        profiles={profiles}
                                        operatorTypes={operatorTypes}
                                        state={'create'}
                                    />
                                    <Grid item xs={12}>
                                        <Box mt={2}>
                                            <Button
                                                type="submit"
                                                fullWidth
                                            >
                                                {t('action.save')}
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </form>
                    </FormProvider>
                    : <NotAuthorizedDialog/>}
            </CustomContentWrapper>
            <CustomLoader loading={isFetching || createOperator.isLoading}/>
        </Box>

    );
};

export default OperatorCreate;

